// Intent callbacks fired into the WASM sim adapter — split out of
// useSimWorld so each file stays under the 400-line limit. useSimWorld
// passes these straight to bootSim(); time controls (pause/resume/
// setSpeed) stay there. Every callback keeps its original useCallback deps,
// so each identity stays stable across renders (the boot effect depends
// on them).

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useCallback } from 'react';
import { reportMoveRejection, useChampionUi } from '../../champion/championStore.js';
import type { Archetype } from '../../ui/DoctrinePanel/index.js';

export interface UseSimIntentsOptions {
  readonly adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>;
  /** Which player slot these intents act for. */
  readonly localPlayerIndex: number;
  readonly publishRef: RefObject<(() => void) | null>;
}

export function useSimIntents(options: UseSimIntentsOptions) {
  const { adapterRef, localPlayerIndex, publishRef } = options;

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const invest = useCallback(
    (archetype: Archetype) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      // Custom AOD intent — declared in the WASM `customIntentKinds` capability.
      // Cast keeps us out of the strict GameIntent union for this exploratory call.
      const response = adapter.intent({
        kind: 'invest_doctrine',
        playerIndex: localPlayerIndex,
        archetype,
      } as never);
      if (!response.ok && import.meta.env.DEV) {
        console.warn('[invest] intent rejected:', response.error);
      }
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const attackCity = useCallback(
    (targetCityId: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      // Custom AOD intent — declared in `customIntentKinds`. Default damage
      // is 10 on the Rust side; we don't surface a damage knob in the UI yet.
      const response = adapter.intent({
        kind: 'attack_city',
        playerIndex: localPlayerIndex,
        targetCityId,
      } as never);
      if (!response.ok && import.meta.env.DEV) {
        console.warn('[attackCity] intent rejected:', response.error);
      }
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const moveUnit = useCallback(
    (unitId: string, targetQ: number, targetR: number) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'move_unit',
        playerIndex: localPlayerIndex,
        unitId,
        targetQ,
        targetR,
      } as never);
      if (!response.ok) reportMoveRejection(response);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const sailShip = useCallback(
    (unitId: string, targetQ: number, targetR: number) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'sail_ship',
        playerIndex: localPlayerIndex,
        unitId,
        targetQ,
        targetR,
      } as never);
      if (!response.ok) reportMoveRejection(response);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const dockTrade = useCallback(
    (unitId: string, cityId: string, food = 'grain') => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'dock_trade',
        playerIndex: localPlayerIndex,
        unitId,
        cityId,
        food,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const acceptMerchant = useCallback(
    (cityId: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'accept_merchant',
        playerIndex: localPlayerIndex,
        cityId,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const declineMerchant = useCallback(
    (cityId: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'decline_merchant',
        playerIndex: localPlayerIndex,
        cityId,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const buildBridge = useCallback(
    (unitId: string, targetQ: number, targetR: number) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'build_bridge',
        playerIndex: localPlayerIndex,
        unitId,
        targetQ,
        targetR,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const startBuilding = useCallback(
    (cityId: string, buildingId: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'start_building',
        playerIndex: localPlayerIndex,
        cityId,
        buildingId,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const setCityDoctrine = useCallback(
    (cityId: string, archetype: Archetype) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'set_city_doctrine',
        playerIndex: localPlayerIndex,
        cityId,
        archetype,
      } as never);
      if (!response.ok && import.meta.env.DEV) {
        console.warn('[setCityDoctrine] intent rejected:', response.error);
      }
      return response.ok;
    },
    [localPlayerIndex],
  );

  // Champion intents (legends.enabled): null = accepted, else the sim's
  // rejection code (packages/protocol/src/legend.ts).
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const legendIntent = useCallback(
    (payload: Record<string, unknown>): string | null => {
      const adapter = adapterRef.current;
      if (!adapter) return 'unavailable';
      const response = adapter.intent({ ...payload, playerIndex: localPlayerIndex } as never);
      if (response.ok) {
        // Firestorm result for the not-at-war hint (champion/castAbility).
        const cast = response.events?.find((e) => e.type === 'legend:ability_activated');
        const hits = (cast?.payload as { hits?: number } | undefined)?.hits;
        useChampionUi.getState().setCastHits(typeof hits === 'number' ? hits : null);
        publishRef.current?.();
        return null;
      }
      return (response.error as { message?: string } | undefined)?.message ?? 'rejected';
    },
    [localPlayerIndex],
  );
  const summonLegend = useCallback(
    (cityId: string) => legendIntent({ kind: 'summon_legend', cityId }),
    [legendIntent],
  );
  const activateLegendAbility = useCallback(
    (unitId: string, slot: number, targetQ: number, targetR: number) =>
      legendIntent({ kind: 'activate_legend_ability', unitId, slot, targetQ, targetR }),
    [legendIntent],
  );

  // Spec §8 suggest mode: pull-only (the sim never executes these);
  // empty array before the adapter boots.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const suggestIntents = useCallback(
    () => adapterRef.current?.suggestIntents(localPlayerIndex) ?? [],
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const deployUnit = useCallback(
    (unitId: string, targetQ: number, targetR: number) => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      const response = adapter.intent({
        kind: 'deploy_unit',
        playerIndex: localPlayerIndex,
        unitId,
        targetQ,
        targetR,
      } as never);
      return response.ok;
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const setFormation = useCallback(
    (unitId: string, formation: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      adapter.intent({
        kind: 'set_formation',
        playerIndex: localPlayerIndex,
        unitId,
        formation,
      } as never);
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const startResearch = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    adapter.intent({
      kind: 'start_research',
      playerIndex: localPlayerIndex,
    } as never);
  }, [localPlayerIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const pilgrimage = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    adapter.intent({
      kind: 'pilgrimage',
      playerIndex: localPlayerIndex,
    } as never);
  }, [localPlayerIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const reform = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    adapter.intent({
      kind: 'reform',
      playerIndex: localPlayerIndex,
    } as never);
  }, [localPlayerIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const activateHeroAbility = useCallback(
    (unitId: string, slotIndex: number) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      adapter.intent({
        kind: 'activate_hero_ability',
        playerIndex: localPlayerIndex,
        unitId,
        slotIndex,
      } as never);
    },
    [localPlayerIndex],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable React refs passed in from useSimWorld
  const selectHeroAbility = useCallback(
    (unitId: string, slotIndex: number, abilityId: string) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      adapter.intent({
        kind: 'select_hero_ability',
        playerIndex: localPlayerIndex,
        unitId,
        slotIndex,
        abilityId,
      } as never);
    },
    [localPlayerIndex],
  );

  return {
    invest,
    attackCity,
    moveUnit,
    sailShip,
    dockTrade,
    acceptMerchant,
    declineMerchant,
    buildBridge,
    startBuilding,
    setCityDoctrine,
    legendIntent,
    summonLegend,
    activateLegendAbility,
    suggestIntents,
    deployUnit,
    setFormation,
    startResearch,
    pilgrimage,
    reform,
    activateHeroAbility,
    selectHeroAbility,
  };
}
